/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import * as monaco from 'monaco-editor';
import { v4 as uuidv4 } from 'uuid';

import { FunctionMetadata, ParsedFunctionCall } from '../types/LiveAgentEditorTypes';

import { DecorationManager } from './DecorationManager';

import { toast } from '@/components/ui/use-toast';
import { RecordedAction, Step as UIStep } from '@/pages/projects/LiveAgentTypes';

const DEBUG_METADATA = false;

/**
 * Manages metadata for function calls in the Live Agent Editor.
 *
 * This class is responsible for tracking and updating metadata related to function calls
 * parsed from the editor's AST. It maintains execution state, position information,
 * and relationships between UI steps and their corresponding code representations.
 */
export class MetadataManager {
    /**
     * Storage for function call metadata, indexed by unique keys.
     * @private
     */
    private metadata: Record<string, FunctionMetadata> = {};

    /**
     * Updates metadata based on parsed function calls from the AST.
     *
     * @param functionCalls - Array of parsed function calls from the editor's code
     * @param steps - Array of UI steps corresponding to the function calls
     * @param init - If true, initializes metadata from steps; if false, updates existing metadata
     * @param decorationManager - Optional DecorationManager to retrieve decoration information
     * @returns Updated metadata record
     */
    updateFromAst(
        functionCalls: ParsedFunctionCall[],
        steps: UIStep[],
        init: boolean,
        decorationManager?: DecorationManager,
    ): Record<string, FunctionMetadata> {
        const newMetadata: Record<string, FunctionMetadata> = {};
        const usedIds: string[] = [];

        if (DEBUG_METADATA) {
            // eslint-disable-next-line no-console
            console.log('==== Updating metadata from AST ====');
        }

        for (let i = 0; i < functionCalls.length; ++i) {
            const call = functionCalls[i];

            if (init) {
                const step = steps[i];

                if (!step) {
                    toast({
                        title: 'Error: Missing step data',
                        description: 'Please check your step data',
                        variant: 'destructive',
                    });
                    continue;
                }

                // Extract actions from the original step if available
                const actions = this.cleanupActions(step?.actions || []);
                const childActions = this.cleanupActions(step?.childActions || []);
                if (DEBUG_METADATA) {
                    // eslint-disable-next-line no-console
                    console.log('Creating metadata for step:', step.id);
                    // eslint-disable-next-line no-console
                    console.log('Call:', call);
                }

                newMetadata[step.id] = {
                    key: step.id,
                    call: structuredClone(call),
                    executed: step.executed || false,
                    running: step.running || false,
                    result: step.result || 'UNKNOWN',
                    message: '',
                    actions: this.metadata[step.id]?.actions || actions,
                    childActions: this.metadata[step.id]?.childActions || childActions,
                    queued: false,
                    active: true,
                };

                usedIds.push(step.id);
            } else {
                // Get decoration options from the decoration manager
                const decorationOptions = decorationManager?.getHealDecorationOptions(call);

                // If we already have a key, use stored metadata, only update positions
                if (
                    decorationOptions?.description?.healKey &&
                    !usedIds.includes(decorationOptions.description.healKey) &&
                    this.metadata[decorationOptions.description.healKey]
                ) {
                    if (DEBUG_METADATA) {
                        // eslint-disable-next-line no-console
                        console.log('Updating metadata for step:', decorationOptions.description.healKey);
                    }

                    const storedMeta = this.metadata[decorationOptions.description.healKey];

                    // Check if arguments or method name have changed, in which case we clear the actions
                    if (
                        (storedMeta.call && JSON.stringify(storedMeta.call.args) !== JSON.stringify(call.args)) ||
                        JSON.stringify(storedMeta.call.methodName) !== JSON.stringify(call.methodName)
                    ) {
                        storedMeta.actions = [];
                        storedMeta.childActions = [];
                        storedMeta.executed = false;
                        storedMeta.running = false;
                        storedMeta.result = 'UNKNOWN';

                        if (DEBUG_METADATA) {
                            // eslint-disable-next-line no-console
                            console.log('Cleared actions for step:', decorationOptions.description.healKey);
                        }
                    }

                    if (DEBUG_METADATA) {
                        // eslint-disable-next-line no-console
                        console.log('Step:', this.metadata[decorationOptions.description.healKey]);
                    }

                    newMetadata[decorationOptions.description.healKey] = {
                        ...storedMeta,
                        call: structuredClone(call),
                        active: true,
                    };

                    usedIds.push(decorationOptions.description.healKey);
                } else {
                    const key = uuidv4();

                    if (DEBUG_METADATA) {
                        // eslint-disable-next-line no-console
                        console.log('Creating new metadata for step:', key);
                    }

                    // If we don't have a key, create a new one
                    newMetadata[key] = {
                        key,
                        call: structuredClone(call),
                        executed: false,
                        running: false,
                        result: 'UNKNOWN',
                        message: '',
                        actions: [],
                        childActions: [],
                        queued: false,
                        active: true,
                    };

                    usedIds.push(key);
                }
            }
        }

        this.metadata = newMetadata;

        return this.metadata;
    }

    /**
     * Retrieves metadata for a specific function call by ID.
     *
     * @param id - Unique identifier for the function call
     * @returns The function metadata if found, undefined otherwise
     */
    getMetadataById(id: string): FunctionMetadata | undefined {
        return this.metadata[id];
    }

    /**
     * Finds metadata for a function call by its line number in the editor.
     *
     * @param lineNumber - Line number in the editor
     * @returns Metadata for the function call at the specified line, if found
     */
    getMetadataByLineNumber(lineNumber: number): FunctionMetadata | undefined {
        const allValues = Object.values(this.metadata).filter(
            (metadata) =>
                metadata.call.decorationsPosition.lineNumberStart <= lineNumber &&
                metadata.call.decorationsPosition.lineNumberEnd >= lineNumber,
        );
        if (DEBUG_METADATA) {
            // eslint-disable-next-line no-console
            console.log('All values:', allValues);
        }
        return allValues[0];
    }

    /**
     * Returns all stored metadata.
     *
     * @returns Record of all function metadata keyed by their unique IDs
     */
    getMetadata(): Record<string, FunctionMetadata> {
        return this.metadata;
    }

    /**
     * Adds new metadata for a function call.
     *
     * @param key - Unique identifier for the metadata
     * @param range - Monaco editor range representing the function's position
     * @param metadata - Existing metadata to be copied and updated
     */
    addMetadata(key: string, range: monaco.IRange, metadata: FunctionMetadata): void {
        const newMetadata: FunctionMetadata = {
            ...metadata,
            key,
            call: structuredClone(metadata.call),
            actions: this.cleanupActions(metadata.actions),
            childActions: this.cleanupActions(metadata.childActions),
        };
        newMetadata.call.functionPosition = {
            lineNumberStart: range.startLineNumber,
            lineNumberEnd: range.endLineNumber,
            columnStart: range.startColumn,
            columnEnd: range.endColumn,
        };
        newMetadata.call.decorationsPosition = {
            lineNumberStart: range.startLineNumber,
            lineNumberEnd: range.endLineNumber,
            columnStart: range.startColumn,
            columnEnd: range.endColumn,
        };

        this.metadata[key] = newMetadata;
    }

    /**
     * Updates the execution status of a step.
     *
     * @param step - UI step with updated status information
     * @param queued - Whether the step is queued for execution
     */
    updateStatus(step: UIStep, queued: boolean): void {
        // Make sure the key exists
        if (!this.metadata[step.id]) {
            return;
        }

        this.metadata[step.id].executed = step.executed || false;
        this.metadata[step.id].running = step.running || false;
        this.metadata[step.id].result = step.result ?? 'UNKNOWN';

        let message = `**Result:** ${step.result as string}`;
        if ((step.result === 'FAIL' || step.result === 'CRASH') && step.actions) {
            for (const action of step.actions) {
                if (action.error) {
                    message = `**Error:** ${action.error}`;
                    break;
                }
            }
        }

        this.metadata[step.id].message = message;
        this.metadata[step.id].actions = this.cleanupActions(step.actions || []);
        this.metadata[step.id].childActions = this.cleanupActions(step.childActions || []);
        this.metadata[step.id].queued = queued;
    }

    private cleanupActions(actions: RecordedAction[]): RecordedAction[] {
        // Keep only the playwright selectors
        const result: RecordedAction[] = [];
        for (const action of actions) {
            const selectors: RecordedAction['selectors'] = {};
            for (const key in action.selectors) {
                if (key.startsWith('playwright')) {
                    selectors[key] = action.selectors[key];
                }
            }
            result.push({
                ...action,
                selectors,
            });
        }
        return result;
    }
}
